// App-owned corrections: do not modify the installed chart library.
export function configureIndicatorCalculations(indicator) {
  const name = indicator.constructor?.className;
  const original = indicator.prepareData;
  if (!['VWAP', 'StochasticMomentumIndex', 'ZigZag', 'VolumeProfile'].includes(name)) return { dispose() {} };
  const prepare = name === 'VolumeProfile' ? function (...args) {
    const result = original.apply(this, args);
    prepareVolumeProfileArea.call(this);
    return result;
  } : name === 'VWAP' ? prepareVWAP
    : name === 'ZigZag' ? prepareZigZag : prepareSMI;
  indicator.prepareData = prepare;
  indicator.markDataDirty?.();
  return { dispose() { if (indicator.prepareData === prepare) indicator.prepareData = original; } };
}

// Keep the installed bucket calculation, correcting only Value Area membership.
// Expand a contiguous interval around the largest-volume bin. Empty bins are
// included once, so the scan cannot jump over the next nonempty price level.
function prepareVolumeProfileArea() {
  const rows = this.series?.data?.values || [];
  if (!rows.length) return;
  const volumes = rows.map(row => Number.isFinite(row.total) && row.total > 0 ? row.total : 0);
  const total = volumes.reduce((sum, value) => sum + value, 0);
  const tolerance = total * 1e-12;
  let peak = 0;
  volumes.forEach((value, index) => {
    if (value > volumes[peak] + tolerance) peak = index;
  });
  const selected = new Set();
  if (total > 0) {
    selected.add(peak);
    let left = peak - 1, right = peak + 1, covered = volumes[peak];
    const target = total * Math.max(0, Math.min(100, this.get('valueArea', 70))) / 100;
    while (covered + tolerance < target && (left >= 0 || right < rows.length)) {
      const below = left >= 0 ? volumes[left] : -Infinity;
      const above = right < rows.length ? volumes[right] : -Infinity;
      // Lowest-index peak and upper-neighbor ties are deterministic even when
      // uniform candle/bin intersections differ by tiny floating-point roundoff.
      const index = left < 0 || (right < rows.length && above + tolerance >= below) ? right++ : left--;
      selected.add(index);
      covered += volumes[index];
    }
  }
  for (const series of new Set([this.series, this.upSeries])) {
    if (!series?.data) continue;
    series.data.values.forEach((row, index) => {
      const area = selected.has(index);
      if (row.area !== area) series.data.setIndex(index, { ...row, area });
    });
    series.columns?.each(column => column._markDirtyKey?.('fillOpacity'));
  }
}

function prepareVWAP() {
  // configureVolumeIndicatorData still restricts this to timestamp-aligned,
  // verified volume. A genuine zero is different from unavailable live volume.
  const prices = this.get('stockSeries')?.dataItems || [];
  const volumes = this.get('volumeSeries')?.dataItems || [];
  const period = this.get('period', Infinity);
  const data = this._getDataArray(prices);
  let totalVolume = 0, totalVW = 0;
  data.forEach((row, i) => {
    const volume = volumes[i]?.get('valueY');
    if (!Number.isFinite(volume) || volume < 0 || !Number.isFinite(row.value_y)) return;
    row.volume = volume;
    row.vw = row.value_y * volume;
    totalVolume += volume;
    totalVW += row.vw;
    if (i >= period) {
      totalVolume -= data[i - period].volume || 0;
      totalVW -= data[i - period].vw || 0;
    }
    row.totalVW = totalVW;
    if (totalVolume > 0) row.vwap = totalVW / totalVolume;
  });
  this.series.updateData(data);
}

function prepareSMI() {
  const items = this.get('stockSeries')?.dataItems || [];
  const period = this.get('period', 10);
  const data = items.map((item, i) => {
    const row = { valueX: item.get('valueX') };
    if (i < period - 1) return row;
    const value = this._getValue(item);
    if (!Number.isFinite(value)) return row;
    let high = -Infinity, low = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      const h = items[j].get('highValueY'), l = items[j].get('lowValueY');
      if (!Number.isFinite(h) || !Number.isFinite(l)) return row;
      high = Math.max(high, h); low = Math.min(low, l);
    }
    row.hhh = value - (high + low) / 2;
    row.dhl = high - low;
    return row;
  });
  const smoothing = this.get('dPeriod', 3);
  this._ema(data, smoothing, 'hhh', 'hhh_ema');
  this._ema(data, smoothing, 'hhh_ema', 'hhh_ema2');
  this._ema(data, smoothing, 'dhl', 'dhl_ema');
  this._ema(data, smoothing, 'dhl_ema', 'dhl_ema2');
  data.forEach(row => {
    if (Number.isFinite(row.hhh_ema2) && row.dhl_ema2 > 0) {
      row.valueS = 200 * row.hhh_ema2 / row.dhl_ema2;
    }
  });
  // The settings promise EMA, so use EMA for the signal as well.
  this._ema(data, this.get('emaPeriod', 3), 'valueS', 'ema');
  this.series.updateData(data);
  this.emaSeries.updateData(data);
}

// Preserve the installed ZigZag threshold/depth rules and series fields.
// The first scan already consumes candle j; replaying j - 1 rewinds pivots
// into the past. Continue at the next unprocessed candle instead.
function prepareZigZag() {
    if (this.series) {
        const deviation = this.get("deviation", 5) / 100;
        const stockSeries = this.get("stockSeries");
        const data = [];
        if (stockSeries) {
            const dataItems = stockSeries.dataItems;
            if (dataItems.length > 1) {
                let firstDataItem = dataItems[0];
                let goesUp = false;
                let firstTime = firstDataItem.get("valueX", 0);
                let firstValue = firstDataItem.get("valueY");
                let firstHigh = firstDataItem.get("highValueY", firstValue);
                let firstLow = firstDataItem.get("lowValueY", firstValue);
                let zigZagTime;
                let zigZagValue;
                let j = 1;
                for (let i = 1, len = dataItems.length; i < len; i++) {
                    let dataItem = dataItems[i];
                    let value = dataItem.get("valueY", 0);
                    let low = dataItem.get("lowValueY", value);
                    let high = dataItem.get("highValueY", value);
                    let time = dataItem.get("valueX", 0);
                    if (low <= Number(firstHigh) * (1 - deviation)) {
                        data.push({ time: firstTime, zigzag: firstHigh });
                        zigZagTime = time;
                        zigZagValue = low;
                        goesUp = true;
                        j = i;
                        break;
                    }
                    else if (high >= Number(firstLow) * (1 + deviation)) {
                        data.push({ time: firstTime, zigzag: firstLow });
                        zigZagTime = time;
                        zigZagValue = high;
                        goesUp = false;
                        j = i;
                        break;
                    }
                }
                if (zigZagValue != null) {
                    let depth = this.get("depth", 3);
                    for (let i = j + 1, len = dataItems.length; i < len; i++) {
                        let dataItem = dataItems[i];
                        let value = dataItem.get("valueY", 0);
                        let low = dataItem.get("lowValueY", value);
                        let high = dataItem.get("highValueY", value);
                        let time = dataItem.get("valueX", 0);
                        if (goesUp) {
                            if (low <= zigZagValue) {
                                zigZagValue = low;
                                zigZagTime = time;
                            }
                            else if (high >= zigZagValue * (1 + deviation)) {
                                let b = Math.max(0, i - depth);
                                let skip = false;
                                for (let k = i; k > b; k--) {
                                    if (high < dataItems[k].get("highValueY")) {
                                        skip = true;
                                        break;
                                    }
                                }
                                if (!skip) {
                                    data.push({ time: zigZagTime, zigzag: zigZagValue });
                                    zigZagValue = high;
                                    zigZagTime = time;
                                    goesUp = false;
                                }
                            }
                        }
                        else {
                            if (high >= zigZagValue) {
                                zigZagValue = high;
                                zigZagTime = time;
                            }
                            else if (low <= zigZagValue * (1 - deviation)) {
                                let b = Math.max(0, i - depth);
                                let skip = false;
                                for (let k = i; k > b; k--) {
                                    if (low > dataItems[k].get("lowValueY")) {
                                        skip = true;
                                        break;
                                    }
                                }
                                if (!skip) {
                                    data.push({ time: zigZagTime, zigzag: zigZagValue });
                                    zigZagValue = low;
                                    zigZagTime = time;
                                    goesUp = true;
                                }
                            }
                        }
                    }
                    const lastDataItem = dataItems[dataItems.length - 1];
                    let lastTime = lastDataItem.get("valueX", 0);
                    let lastValue = lastDataItem.get("valueY");
                    let lastHigh = lastDataItem.get("highValueY", lastValue);
                    let lastLow = lastDataItem.get("lowValueY", lastValue);
                    if (zigZagValue < lastHigh) {
                        data.push({ time: lastTime, zigzag: lastLow });
                    }
                    else {
                        data.push({ time: lastTime, zigzag: lastHigh });
                    }
                }
            }
            const series = this.series;
            if (series.data) {
                // updateData trims rendered items but retains the source-list
                // tail. Remove through ListData first so live pivot changes
                // cannot accumulate retained rows on every contraction/regrowth.
                if (series.dataItems && series.dataItems.length !== series.data.length) {
                    // Repair an output already shortened without its source list.
                    series.data.setAll(data);
                    return;
                }
                while (series.data.length > data.length) series.data.removeIndex(series.data.length - 1);
            }
            series.updateData(data);
        }
    }
}
